import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const formatCurrency = (val) => {
  if (val == null) return '–';
  return `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const winRateColor = (pct) => {
  if (pct >= 60) return 'text-green-600';
  if (pct >= 50) return 'text-amber-600';
  return 'text-red-600';
};

const NiftySignalDashboardPage = () => {
  const [searchParams] = useSearchParams();
  const isMobileEmbed = searchParams.get('tv_embed') === '1';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const url = `/api/v1/nifty/signal-dashboard${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);
      if (response.data?.success && response.data?.data) {
        setData(response.data.data);
      } else {
        setData(null);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('Error fetching NIFTY signal dashboard:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex flex-col">
        {!isMobileEmbed && <Navbar />}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading NIFTY Signal Analysis Dashboard...</p>
          </div>
        </div>
        {!isMobileEmbed && <Footer />}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {!isMobileEmbed && <Navbar />}
      <div className={`flex-1 container mx-auto px-4 max-w-7xl ${isMobileEmbed ? 'py-3' : 'py-8'}`}>
        <div
          className={`mb-6 flex flex-wrap items-center gap-4 ${isMobileEmbed ? 'justify-end' : 'justify-between'}`}
        >
          {!isMobileEmbed && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900">NIFTY Signal Analysis Dashboard</h1>
              <p className="text-gray-600 mt-1">Strategy performance, time-of-day insights, and system metrics from signal history</p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={fetchDashboard}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Refreshing…' : 'Apply'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {!data && !error && (
          <div className="text-center py-12 text-gray-500">No dashboard data available.</div>
        )}

        {data && (
          <>
            {/* Overall summary */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Overall System Performance</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Total Trades</p>
                  <p className="text-2xl font-bold text-gray-900">{data.overall?.total_trades ?? '–'}</p>
                </div>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Win Rate</p>
                  <p className={`text-2xl font-bold ${winRateColor(data.overall?.win_rate_pct)}`}>
                    {data.overall?.win_rate_pct != null ? `${data.overall.win_rate_pct}%` : '–'}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Net P&L</p>
                  <p className={`text-2xl font-bold ${(data.overall?.net_profit ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(data.overall?.net_profit)}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Reward:Risk</p>
                  <p className="text-2xl font-bold text-gray-900">{data.overall?.reward_risk_ratio ?? '–'}</p>
                </div>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Expectancy</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.overall?.expectancy)}</p>
                </div>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Max Consec. Losses</p>
                  <p className="text-2xl font-bold text-red-600">{data.overall?.max_consecutive_losses ?? '–'}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Best Day</p>
                  <p className="font-semibold text-green-600">
                    {data.overall?.best_day ? `${data.overall.best_day.date} (${formatCurrency(data.overall.best_day.net_pl)})` : '–'}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Worst Day</p>
                  <p className="font-semibold text-red-600">
                    {data.overall?.worst_day ? `${data.overall.worst_day.date} (${formatCurrency(data.overall.worst_day.net_pl)})` : '–'}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Trades / Day</p>
                  <p className="font-semibold text-gray-900">{data.overall?.trades_per_day ?? '–'}</p>
                </div>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">Avg Pts (Win / Loss)</p>
                  <p className="font-semibold text-gray-900">
                    {data.overall?.avg_points_captured != null || data.overall?.avg_points_lost != null
                      ? `${data.overall.avg_points_captured ?? '–'} / ${data.overall.avg_points_lost ?? '–'}`
                      : '–'}
                  </p>
                </div>
              </div>
            </section>

            {/* Strategy performance */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Strategy Performance</h2>
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Strategy</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Trades</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Win Rate</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg P&L</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(data.strategy_performance || []).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.strategy_name || row.strategy_type}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.trades}</td>
                        <td className={`px-4 py-3 text-sm text-right font-medium ${winRateColor(row.win_rate_pct)}`}>{row.win_rate_pct}%</td>
                        <td className={`px-4 py-3 text-sm text-right ${(row.avg_pl ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(row.avg_pl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Time of day + 30-min blocks */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Time of Day (IST)</h2>
                <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Trades</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Win Rate</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg P&L</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(data.time_of_day || []).map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{row.label}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{row.trades}</td>
                          <td className={`px-4 py-2 text-sm text-right ${winRateColor(row.win_rate_pct)}`}>{row.win_rate_pct}%</td>
                          <td className={`px-4 py-2 text-sm text-right ${(row.avg_pl ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(row.avg_pl)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.time_insights && (
                  <div className="mt-3 flex flex-wrap gap-4 text-sm">
                    <span className="text-green-700">Best: {data.time_insights.best_window}</span>
                    <span className="text-red-700">Worst: {data.time_insights.worst_window}</span>
                  </div>
                )}
              </section>
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">30-Minute Time Blocks</h2>
                <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Block</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Trades</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Win Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(data.time_blocks_30min || []).map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{row.label}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{row.trades}</td>
                          <td className={`px-4 py-2 text-sm text-right ${winRateColor(row.win_rate_pct)}`}>{row.win_rate_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            {/* Day of week */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Day of Week</h2>
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden max-w-2xl">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Day</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Trades</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Win Rate</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg P&L</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(data.day_of_week || []).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.day_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.trades}</td>
                        <td className={`px-4 py-3 text-sm text-right ${winRateColor(row.win_rate_pct)}`}>{row.win_rate_pct}%</td>
                        <td className={`px-4 py-3 text-sm text-right ${(row.avg_pl ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(row.avg_pl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Best strategy + time combo */}
            {data.best_strategy_time_combo && (
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Best Strategy + Time Combo</h2>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 max-w-md">
                  <p className="font-medium text-indigo-900">{data.best_strategy_time_combo.label}</p>
                  <p className="text-sm text-indigo-700 mt-1">
                    {data.best_strategy_time_combo.trades} trades, {data.best_strategy_time_combo.win_rate_pct}% win rate, {formatCurrency(data.best_strategy_time_combo.avg_pl)} avg P&L
                  </p>
                </div>
              </section>
            )}

            {/* Improvement over time */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Improvement Over Time</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm font-medium text-gray-700">First Half (chronological)</p>
                  <p className="text-sm text-gray-500">{data.improvement_over_time?.first_half?.trades ?? 0} trades</p>
                  <p className={`font-semibold ${winRateColor(data.improvement_over_time?.first_half?.win_rate_pct)}`}>
                    Win rate: {data.improvement_over_time?.first_half?.win_rate_pct ?? '–'}%
                  </p>
                  <p className="text-sm text-red-600">Loss rate: {data.improvement_over_time?.first_half?.loss_rate_pct ?? '–'}%</p>
                </div>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <p className="text-sm font-medium text-gray-700">Second Half (chronological)</p>
                  <p className="text-sm text-gray-500">{data.improvement_over_time?.second_half?.trades ?? 0} trades</p>
                  <p className={`font-semibold ${winRateColor(data.improvement_over_time?.second_half?.win_rate_pct)}`}>
                    Win rate: {data.improvement_over_time?.second_half?.win_rate_pct ?? '–'}%
                  </p>
                  <p className="text-sm text-red-600">Loss rate: {data.improvement_over_time?.second_half?.loss_rate_pct ?? '–'}%</p>
                </div>
              </div>
            </section>

            {/* Weekly trend */}
            {(data.weekly_trend?.length > 0) && (
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Weekly Trend (Loss Rate)</h2>
                <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Week Starting</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Trades</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Losses</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Loss Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.weekly_trend.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{row.week_start}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{row.trades}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{row.losses}</td>
                          <td className={`px-4 py-2 text-sm text-right ${(row.loss_rate_pct ?? 0) <= 35 ? 'text-green-600' : (row.loss_rate_pct <= 45 ? 'text-amber-600' : 'text-red-600')}`}>{row.loss_rate_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Suggested improvements */}
            {data.suggested_improvements && (
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Suggested AI Improvements</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Strategy Weights</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {data.suggested_improvements.strategy_weights && Object.entries(data.suggested_improvements.strategy_weights).map(([k, v]) => (
                        <li key={k}>{k.replace(/_/g, ' ')}: {v}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Time Filters</p>
                    <p className="text-sm text-green-700">Preferred: {(data.suggested_improvements.time_filters?.preferred || []).join(', ')}</p>
                    <p className="text-sm text-red-700 mt-1">Avoid: {(data.suggested_improvements.time_filters?.avoid || []).join(', ')}</p>
                  </div>
                </div>
                {data.suggested_improvements.insights?.length > 0 && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-amber-900 mb-2">Insights</p>
                    <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
                      {data.suggested_improvements.insights.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
      {!isMobileEmbed && <Footer />}
    </div>
  );
};

export default NiftySignalDashboardPage;
